/**
 * SponsorAssets — inline SVG fallbacks for V1 demo.
 *
 * Real sponsor creative URLs aren't ready yet. Rather than ship broken
 * <img> tags, the CMS-shaped `Creative.assetUrl` accepts an `inline:<key>`
 * sentinel that resolves to a hand-rolled SVG. When real CDN assets land,
 * sponsors.ts swaps the sentinel for a URL and <SponsorLogo> falls back to
 * an <img> automatically — components downstream don't change.
 *
 * Adding a new sponsor: add a `<NewSponsorMark>` + `<NewSponsorWordmark>`
 * here, extend the resolver switch, and reference `inline:newsponsor-*`
 * from sponsors.ts.
 */
import { FC } from "react";

/* Real brand SVGs the site header uses — sourced from the same files
   instead of a hand-rolled approximation so the iframe fallback shows
   the official Allstar mark + wordmark in correct proportions. */
import { ReactComponent as HeaderLogoSvg } from "../../../../../../../assets/images/logo/headerLogo.svg";
import { ReactComponent as AMarkSvg } from "../../../../../../../assets/images/logo/aMark.svg";

import type { Creative } from "../../@types";

const LEETIFY_PINK = "#FF2864";
const GFUEL_YELLOW = "#FFD200";
const GFUEL_BLACK = "#0a0a0d";
const HYPERX_RED = "#E4002B";
const NVIDIA_GREEN = "#76B900";
const ALLSTAR_GREEN = "#3C87D2";

interface AssetSizeProps {
  width?: number | string;
  height?: number | string;
  title?: string;
  className?: string;
}

/**
 * Pink "L↗" glyph alone, no background. For overlays where the logo sits on
 * the video and needs to read as a watermark.
 */
export const LeetifyMark: FC<AssetSizeProps> = ({
  width = 32,
  height,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "Leetify"}
    className={className}
  >
    {/* Stylized angular L */}
    <polygon points="5,4 12,4 12,22 25,22 25,28 5,28" fill={LEETIFY_PINK} />
    {/* Up-right arrow wedge merged with the top of the L */}
    <polygon points="14,4 26,4 26,16 20,16 20,10 14,10" fill={LEETIFY_PINK} />
  </svg>
);

/**
 * Pink mark + white "leetify" wordmark. Used for upper-left watermark and
 * lower-right player-chrome attribution.
 */
export const LeetifyWordmark: FC<AssetSizeProps> = ({
  width = 160,
  height,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 200 56"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "Leetify"}
    className={className}
  >
    {/* Mark, vertically centered */}
    <polygon points="6,12 16,12 16,38 36,38 36,46 6,46" fill={LEETIFY_PINK} />
    <polygon points="20,12 38,12 38,30 30,30 30,20 20,20" fill={LEETIFY_PINK} />
    {/* Wordmark */}
    <text
      x="50"
      y="42"
      fontFamily="Inter, system-ui, -apple-system, Segoe UI, sans-serif"
      fontWeight="800"
      fontSize="32"
      fill="#fff"
      fontStyle="italic"
      letterSpacing="-1"
    >
      leetify
    </text>
  </svg>
);

/**
 * Dark rounded tile with the pink mark inside. Used as the leading icon in
 * the floating top banner.
 */
export const LeetifyIconTile: FC<AssetSizeProps> = ({
  width = 40,
  height = 40,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 40 40"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "Leetify"}
    className={className}
  >
    <rect width="40" height="40" rx="8" fill="#1c1c22" />
    <polygon points="10,10 16,10 16,26 28,26 28,32 10,32" fill={LEETIFY_PINK} />
    <polygon points="18,10 30,10 30,22 24,22 24,16 18,16" fill={LEETIFY_PINK} />
  </svg>
);

/* ---------- G FUEL ---------- */

export const GFuelMark: FC<AssetSizeProps> = ({
  width = 32,
  height,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "G FUEL"}
    className={className}
  >
    <rect width="32" height="32" rx="6" fill={GFUEL_BLACK} />
    <text
      x="16"
      y="23"
      textAnchor="middle"
      fontFamily="Inter, system-ui, sans-serif"
      fontWeight="900"
      fontSize="22"
      fill={GFUEL_YELLOW}
      fontStyle="italic"
    >
      G
    </text>
  </svg>
);

export const GFuelWordmark: FC<AssetSizeProps> = ({
  width = 160,
  height,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 220 56"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "G FUEL"}
    className={className}
  >
    <rect x="2" y="10" width="36" height="36" rx="6" fill={GFUEL_YELLOW} />
    <text
      x="20"
      y="38"
      textAnchor="middle"
      fontFamily="Inter, system-ui, sans-serif"
      fontWeight="900"
      fontSize="26"
      fill={GFUEL_BLACK}
      fontStyle="italic"
    >
      G
    </text>
    <text
      x="48"
      y="40"
      fontFamily="Inter, system-ui, sans-serif"
      fontWeight="900"
      fontSize="26"
      fill="#fff"
      fontStyle="italic"
      letterSpacing="1"
    >
      FUEL
    </text>
  </svg>
);

export const GFuelIconTile: FC<AssetSizeProps> = ({
  width = 40,
  height = 40,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 40 40"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "G FUEL"}
    className={className}
  >
    <rect width="40" height="40" rx="8" fill={GFUEL_YELLOW} />
    <text
      x="20"
      y="29"
      textAnchor="middle"
      fontFamily="Inter, system-ui, sans-serif"
      fontWeight="900"
      fontSize="26"
      fill={GFUEL_BLACK}
      fontStyle="italic"
    >
      G
    </text>
  </svg>
);

/* ---------- HyperX ---------- */

export const HyperXMark: FC<AssetSizeProps> = ({
  width = 32,
  height,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "HyperX"}
    className={className}
  >
    <polygon points="4,6 12,6 18,16 12,26 4,26 10,16" fill={HYPERX_RED} />
    <polygon points="14,6 22,6 28,16 22,26 14,26 20,16" fill={HYPERX_RED} />
  </svg>
);

export const HyperXWordmark: FC<AssetSizeProps> = ({
  width = 160,
  height,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 240 56"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "HyperX"}
    className={className}
  >
    <polygon points="6,14 14,14 20,28 14,42 6,42 12,28" fill={HYPERX_RED} />
    <polygon points="16,14 24,14 30,28 24,42 16,42 22,28" fill={HYPERX_RED} />
    <text
      x="40"
      y="40"
      fontFamily="Inter, system-ui, sans-serif"
      fontWeight="900"
      fontSize="28"
      fill="#fff"
      letterSpacing="-0.5"
    >
      HyperX
    </text>
  </svg>
);

export const HyperXIconTile: FC<AssetSizeProps> = ({
  width = 40,
  height = 40,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 40 40"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "HyperX"}
    className={className}
  >
    <rect width="40" height="40" rx="8" fill="#1c1c22" />
    <polygon points="8,10 14,10 20,20 14,30 8,30 13,20" fill={HYPERX_RED} />
    <polygon points="18,10 24,10 30,20 24,30 18,30 23,20" fill={HYPERX_RED} />
  </svg>
);

/* ---------- NVIDIA GeForce ---------- */

export const NvidiaMark: FC<AssetSizeProps> = ({
  width = 32,
  height,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "NVIDIA GeForce"}
    className={className}
  >
    <circle cx="16" cy="16" r="14" fill={NVIDIA_GREEN} />
    <path
      d="M10 11 q6 -2 12 5 q-6 -1 -12 -5 z M10 21 q6 2 12 -5 q-6 1 -12 5 z"
      fill="#0a0a0d"
    />
  </svg>
);

export const NvidiaWordmark: FC<AssetSizeProps> = ({
  width = 180,
  height,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 260 56"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "NVIDIA GeForce"}
    className={className}
  >
    <circle cx="22" cy="28" r="18" fill={NVIDIA_GREEN} />
    <path
      d="M14 22 q8 -3 16 6 q-8 -1 -16 -6 z M14 34 q8 3 16 -6 q-8 1 -16 6 z"
      fill="#0a0a0d"
    />
    <text
      x="48"
      y="35"
      fontFamily="Inter, system-ui, sans-serif"
      fontWeight="800"
      fontSize="22"
      fill="#fff"
      letterSpacing="0.5"
    >
      GeForce
    </text>
    <text
      x="48"
      y="48"
      fontFamily="Inter, system-ui, sans-serif"
      fontWeight="700"
      fontSize="9"
      fill={NVIDIA_GREEN}
      letterSpacing="2.5"
    >
      NVIDIA
    </text>
  </svg>
);

export const NvidiaIconTile: FC<AssetSizeProps> = ({
  width = 40,
  height = 40,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 40 40"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "NVIDIA GeForce"}
    className={className}
  >
    <rect width="40" height="40" rx="8" fill="#0a0a0d" />
    <circle cx="20" cy="20" r="13" fill={NVIDIA_GREEN} />
    <path
      d="M14 16 q6 -2 12 4 q-6 -1 -12 -4 z M14 24 q6 2 12 -4 q-6 1 -12 4 z"
      fill="#0a0a0d"
    />
  </svg>
);

/* ---------- Allstar (us, fallback brand) ---------- */

/**
 * Real Allstar marks — same SVGs the site header uses, imported instead
 * of hand-rolled so the iframe fallback shows the official brand asset
 * in correct proportions. White wordmark + envy-green star reads cleanly
 * on the dark video surface without any color overrides.
 *
 * Native viewBoxes:
 *   - HeaderLogoSvg: 120×24  (wordmark + mark composition, ~5:1)
 *   - AMarkSvg:       45×82  (star alone, tall portrait)
 */
export const AllstarMark: FC<AssetSizeProps> = ({
  width = 24,
  height,
  title,
  className,
}) => (
  <AMarkSvg
    width={width}
    height={height}
    aria-label={title || "Allstar"}
    role="img"
    className={className}
  />
);

export const AllstarWordmark: FC<AssetSizeProps> = ({
  width = 160,
  height,
  title,
  className,
}) => (
  <HeaderLogoSvg
    width={width}
    height={height}
    aria-label={title || "Allstar"}
    role="img"
    className={className}
  />
);

/** Dark rounded tile housing a simplified star — used by top-banner-style
    slots. V1 fallback never fills the banner so this exists only for API
    parity with the other sponsor asset sets; kept hand-rolled (rather than
    nesting the real AMark svg) to avoid SVG-in-SVG positioning quirks. */
export const AllstarIconTile: FC<AssetSizeProps> = ({
  width = 40,
  height = 40,
  title,
  className,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 40 40"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title || "Allstar"}
    className={className}
  >
    <rect width="40" height="40" rx="8" fill="#1c1c22" />
    <polygon
      points="20,7 24,16.5 34,17.5 26.5,24.5 28.7,34 20,29 11.3,34 13.5,24.5 6,17.5 16,16.5"
      fill={ALLSTAR_GREEN}
    />
  </svg>
);

type SponsorLogoVariant = "wordmark" | "mark" | "iconTile";

interface SponsorLogoProps {
  creative: Pick<Creative, "assetUrl" | "altText">;
  variant?: SponsorLogoVariant;
  width?: number | string;
  height?: number | string;
  className?: string;
}

/**
 * Resolver that maps a Creative to the right rendered asset. `inline:<key>`
 * sentinels render the matching SVG; anything else renders an <img>.
 */
export const SponsorLogo: FC<SponsorLogoProps> = ({
  creative,
  variant = "wordmark",
  width,
  height,
  className,
}) => {
  const { assetUrl, altText } = creative;

  if (assetUrl.startsWith("inline:leetify-wordmark")) {
    return (
      <LeetifyWordmark
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:leetify-mark")) {
    return (
      <LeetifyMark
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:leetify-icon-tile")) {
    return (
      <LeetifyIconTile
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:gfuel-wordmark")) {
    return (
      <GFuelWordmark
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:gfuel-mark")) {
    return (
      <GFuelMark
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:gfuel-icon-tile")) {
    return (
      <GFuelIconTile
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:hyperx-wordmark")) {
    return (
      <HyperXWordmark
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:hyperx-mark")) {
    return (
      <HyperXMark
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:hyperx-icon-tile")) {
    return (
      <HyperXIconTile
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:nvidia-wordmark")) {
    return (
      <NvidiaWordmark
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:nvidia-mark")) {
    return (
      <NvidiaMark
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:nvidia-icon-tile")) {
    return (
      <NvidiaIconTile
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:allstar-wordmark")) {
    return (
      <AllstarWordmark
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:allstar-mark")) {
    return (
      <AllstarMark
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }
  if (assetUrl.startsWith("inline:allstar-icon-tile")) {
    return (
      <AllstarIconTile
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }

  // Variant-driven inline fallback when sentinel doesn't match a known key.
  if (assetUrl.startsWith("inline:")) {
    if (variant === "iconTile")
      return (
        <LeetifyIconTile
          width={width}
          height={height}
          title={altText}
          className={className}
        />
      );
    if (variant === "mark")
      return (
        <LeetifyMark
          width={width}
          height={height}
          title={altText}
          className={className}
        />
      );
    return (
      <LeetifyWordmark
        width={width}
        height={height}
        title={altText}
        className={className}
      />
    );
  }

  // Real CDN asset.
  return (
    <img
      src={assetUrl}
      alt={altText}
      width={width}
      height={height}
      className={className}
    />
  );
};
